import {useEffect, useState} from 'react';
import qna from '@bizzabo/api/attendee/qna';
import moment from 'moment';
import {getAccessTokenName} from '../../../../../AuthToken';
import config from '../../../../../config/config';
import {getDomainAdjustedPath} from '../../../../../bizzaboAjax';
import {getTimeToSessionEndWithDelayMs, SESSION_GRACE_TIME} from '../../sessionBroadcastUtils';

const tokenCookieName = getAccessTokenName();
const refreshTokenUrl = getDomainAdjustedPath('/userProfile/oauth/refreshToken');
const httpConfig = {tokenCookieName, baseUrl: config.apiServer, refreshTokenUrl};
const http = qna.http(httpConfig);

const useQna = (event, session, user, enabled = true) => {
    const [questions, setQuestions] = useState([]);
    const [qnaEntity, setQnaEntity] = useState({});
    const [settings, setSettings] = useState({
        enableVote: null,
        enableQuestionSubmission: null,
        enableAnonymousQuestions: null,
    });
    const socketConfig = {
        tokenCookieName,
        baseUrl: config.realtimeNotificationsHost,
        refreshTokenUrl,
        accountId: event['account-id'],
        eventId: event.id
    };
    const socket = qna.socket(socketConfig);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastQuestion, setLastQuestion] = useState(null);
    const [sortOrder, setSortOrder] = useState({VOTE_COUNT: 'DESC'});
    useEffect(() => {
        if (!enabled) return;
        const init = async() => {
            try {
                const qnaRes = await http.getBySession({eventId: event.id, sessionId: session.id});

                setQnaEntity(qnaRes || {});
                await Promise.all([getSettings(qnaRes), realtime(qnaRes), getQuestions(qnaRes)]);
            } catch (e) {
                console.log(e.message);
            } finally {
                setLoading(false);
            }
        };
        init();
        closeConnectionTimeout();

        return () => {
            socket.close();
        };
    }, [enabled]);

    useEffect(() => {
        if (lastQuestion) {
            updateSortedQuestions(lastQuestion);
        }
        setLastQuestion(null);
    }, [lastQuestion]);

    const getSettings = async(qnaEntity) => {
        try {
            const {enableVote, enableQuestionSubmission, enableAnonymousQuestions, moderatorName} = await http.getSettings({
                eventId: event.id,
                qnaId: qnaEntity.id
            });
            setSettings({
                enableVote,
                enableQuestionSubmission,
                enableAnonymousQuestions,
                moderatorName,
            });
        } catch (e) {
            console.error(e.message);
        }
    };
    const realtime = async(qnaEntity) => {
        const clientFacing = true;
        try {
            const tryToConnect = async(fallbackInterval = 0) => {
                await socket.connect(event['account-id'], event.id, 'qna', {
                    fallback: () => fallback(fallbackInterval),
                    onConnected: () => clearInterval(fallbackInterval),
                    secondsToInitResetCount: 300
                }, clientFacing);
            };
            const fallback = async(fallbackInterval) => {
                clearInterval(fallbackInterval);
                let pollingInterval = setInterval(() => getQuestions(qnaEntity), 30000);
                setTimeout(async() => await tryToConnect(pollingInterval), 30000);
            };
            await tryToConnect();
            socket.onQuestionUpdate({
                entityId: qnaEntity.id,
                cb: updateStatus
            });

            socket.onQuestionApprove({
                entityId: qnaEntity.id,
                cb: setLastQuestion
            });

            socket.onSettingsUpdate({
                entityId: qnaEntity.id,
                cb: (newSettings) => {
                    setSettings({...settings, ...newSettings});
                }
            });

        } catch (e) {
            console.log(e.message);
        }
    };

    const closeConnectionTimeout = () => {
        const timeZone = event && event['time-zone-id'];
        const closeConnectionsAfter = getTimeToSessionEndWithDelayMs(session, SESSION_GRACE_TIME, timeZone);
        setTimeout(()=>{
            socket.close();
        }, Math.max(closeConnectionsAfter, 0));
    };

    const getQuestions = async(qna, newSortOrder) => {
        setLoading(true);
        if (!qna) {
            qna = qnaEntity;
        }
        if (!newSortOrder) {
            newSortOrder = {VOTE_COUNT: 'DESC'};
        }
        setSortOrder(newSortOrder);
        try {
            const highlightedRes = await http.getHighlighted({eventId: event.id, qnaId: qna.id});
            const questionsRes = await http.search({eventId: event.id, qnaId: qna.id, orders: newSortOrder});

            const fetchedQuestions = [];
            if (highlightedRes) {
                fetchedQuestions.push(highlightedRes);
            }

            if (questionsRes?.entities) {
                fetchedQuestions.push(...questionsRes?.entities);
            }

            setQuestions(fetchedQuestions);
            setLoading(false);
        } catch (e) {
            console.log(e.message);
        } finally {
            setLoading(false);
        }
    };

    const createQuestion = async({text, anonymous}) => {
        setLoading(true);
        try {
            const questionRes = await http.create({
                eventId: event.id,
                qnaId: qnaEntity.id,
                text,
                anonymous
            });

            updateSortedQuestions(questionRes);
        } catch (e) {
            console.log(e.message);
        } finally {
            setLoading(false);
        }
    };

    const upvoteQuestion = async({id}) => {
        try {
            await http.upvote({
                eventId: event.id,
                qnaId: qnaEntity.id,
                id
            });
        } catch (e) {
            console.log(e.message);
        }
    };

    const cancelUpvoteQuestion = async({ id }) => {
        try {
            await http.cancelUpvote({
                eventId: event.id,
                qnaId: qnaEntity.id,
                id
            });
        } catch (e) {
            console.log(e.message);
        }
    };

    const updateStatus = async(question) => {
        if (question.status === 'APPROVED') {
            return;
        }

        let found = false;

        const changeStatus = q => {
            if (question.answerLive !== undefined) {
                q.answerLive = false;
            }

            if (q.id === question.questionId) {
                found = true;
                if (question.status !== undefined) {
                    q.status = question.status;
                    if (q.status !== 'APPROVED' && q.askedBy !== user.id) {
                        return null;
                    }
                }
                if (question.answerLive !== undefined) {
                    q.answerLive = question.answerLive;
                }
                if (question.answered !== undefined) {
                    q.answered = question.answered;
                }
                if (question.answerText !== undefined) {
                    q.answerText = question.answerText;
                    q.answeredAt = question.answeredAt;
                }
                if (question.voteCount !== undefined) {
                    let isVoteCountChanged = q.voteCount !== question.voteCount;
                    q.voteCount = question.voteCount;
                    q.isVotedByMe = user.id === question.userProfileId ? !!question.isUpvote : q.isVotedByMe;
                    if (isVoteCountChanged) {
                        setLastQuestion(q);
                    }
                }
            }

            return q;
        };
        setQuestions(questions => questions
            .map(changeStatus)
            .filter(q => q)
        );
    };

    const updateSortedQuestions = (question) => {
        const insertQuestion = (questions) => {
            if (questions.length === 0) {
                return [question];
            }
            let insert = false;
            let resultQuestions = [...questions];
            let index = questions.findIndex((q) => q.id === question.id);
            if (index !== -1) {
                resultQuestions.splice(index, 1);
            }
            for (const [i, q] of resultQuestions.entries()) {
                if (sortOrder.CREATED || +question.voteCount === +q.voteCount) {
                    let created = moment(question.created);
                    insert = created.isSame(q.created) || created.isAfter(q.created);
                } else {
                    insert = +question.voteCount > +q.voteCount;
                }
                if (insert) {
                    resultQuestions.splice(i, 0, question);
                    break;
                } else if (i === resultQuestions.length - 1) {
                    resultQuestions.push(question);
                    break;
                }
            }
            return resultQuestions.length ? resultQuestions : [question];
        };

        setQuestions(insertQuestion);
    };

    return {
        questions,
        qnaEntity,
        createQuestion,
        getQuestions,
        upvoteQuestion,
        cancelUpvoteQuestion,
        settings,
        loading,
        error
    };

};

export default useQna;
